"use client";

import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { api, loadTokens } from "@/lib/api";

const STORAGE_KEY = "granbay.theme";

type ThemePref = "system" | "light" | "dark";
type ResolvedTheme = "light" | "dark";

/**
 * STORY-1121 — theme toggle. Phase 1 (#302) shipped localStorage-
 * only; phase 2 wires server-side persistence (`users.theme_pref`)
 * so the pref follows the user across devices / fresh logins /
 * incognito.
 *
 * Read order on mount:
 *   1. localStorage — instant, no round-trip (matches the no-flash
 *      bootstrap in app/layout.tsx).
 *   2. /v1/me.theme_pref — fallback for fresh-device hydration.
 *      When server has a pref and localStorage doesn't, sync
 *      localStorage so subsequent loads stay zero-round-trip.
 *
 * Write order on toggle:
 *   1. Update DOM data-theme + state immediately.
 *   2. Persist to localStorage (synchronous; must succeed for
 *      no-flash on next reload).
 *   3. Fire-and-forget PATCH /v1/me/theme so server side stays in
 *      sync. Failure is non-blocking — the UX already updated.
 */
export function ThemeToggle() {
  const { t } = useTranslation();
  const [pref, setPref] = useState<ThemePref>("system");
  const [resolved, setResolved] = useState<ResolvedTheme>("dark");

  // Mount: read localStorage first, then fall back to /v1/me when no
  // local pref exists (fresh-login / incognito / different device).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored === "light" || stored === "dark" || stored === "system") {
          if (!cancelled) setPref(stored);
          return;
        }
      } catch {
        // SSR / disabled storage — fall through to server fetch
      }

      if (!loadTokens()) return; // logged out — keep default
      try {
        const me = await api.me();
        if (cancelled) return;
        const serverPref = me.theme_pref;
        if (
          serverPref === "light" ||
          serverPref === "dark" ||
          serverPref === "system"
        ) {
          setPref(serverPref);
          // Backfill localStorage so the next no-flash bootstrap
          // hits zero round-trip.
          try {
            window.localStorage.setItem(STORAGE_KEY, serverPref);
          } catch {
            // Disabled storage; harmless
          }
        }
      } catch {
        // /v1/me failed — keep default; non-fatal for theme
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Apply pref to <html data-theme="..."> + listen to system changes
  // when pref="system".
  useEffect(() => {
    const mql = window.matchMedia("(prefers-color-scheme: light)");
    const computeResolved = (): ResolvedTheme =>
      pref === "system" ? (mql.matches ? "light" : "dark") : pref;

    const apply = () => {
      const next = computeResolved();
      setResolved(next);
      document.documentElement.setAttribute("data-theme", next);
    };
    apply();

    if (pref === "system") {
      const onChange = () => apply();
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
  }, [pref]);

  const update = (next: ThemePref) => {
    setPref(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Disabled storage — pref is in-memory only for this session
    }
    // Phase 2: fire-and-forget server persist. Failure is
    // non-blocking — the UX already updated, localStorage already
    // wrote, the next reload still no-flashes correctly. Server-
    // side pref is the cross-device sync mechanism.
    if (loadTokens()) {
      void api.updateThemePref(next).catch(() => {
        // Silently swallow — server is best-effort
      });
    }
  };

  // Localized resolved-theme noun for the "Currently rendering as
  // <strong>X</strong>" line. The localized labels also drive the
  // toggle button text below.
  const optLabels: Record<ThemePref, string> = {
    system: t("themeToggle.system"),
    dark: t("themeToggle.dark"),
    light: t("themeToggle.light"),
  };

  return (
    <div>
      <p className="mb-2 text-[12px] font-bold">{t("themeToggle.label")}</p>
      <p className="mb-3 text-[11px] text-[var(--color-text3)]">
        <Trans
          i18nKey="themeToggle.currentlyRendering"
          values={{ resolved: optLabels[resolved] }}
          components={{ strong: <strong /> }}
        />
        {pref === "system" && t("themeToggle.followingOs")}.
      </p>
      <div className="inline-flex rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-[2px]">
        {(["system", "dark", "light"] as const).map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => update(opt)}
            aria-pressed={pref === opt}
            className="rounded px-3 py-1.5 text-[12px] font-medium transition-colors"
            style={
              pref === opt
                ? {
                    background: "var(--color-brand-tint)",
                    color: "var(--color-brand)",
                  }
                : { color: "var(--color-text3)" }
            }
          >
            {optLabels[opt]}
          </button>
        ))}
      </div>
    </div>
  );
}
